import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { KlipyService } from '../../../../service/klipy.service';


@Component({
  selector: 'app-gif-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  styleUrls: ['./gif-panel.component.css'],
  template: `
    <div class="gif-panel">

      <!-- Search Bar -->
      <div class="gif-search-bar">
        <input
          type="text"
          class="form-control"
          placeholder="Search GIFs"
          [(ngModel)]="gifQuery"
          (keyup.enter)="searchGifs()"
        />
        <button
          type="button"
          class="btn btn-primary btn-sm gif-search-btn"
          (click)="searchGifs()"
          [disabled]="!gifQuery || gifQuery.trim() === ''"
        >
          Search
        </button>
        <button
          type="button"
          class="gif-close-btn"
          (click)="closed.emit()"
          title="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
            viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <!-- Loading Skeleton -->
      @if (isLoading) {
        <div class="gif-loading-skeleton">
          @for (i of skeletonItems; track i) {
            <div class="gif-skeleton-item">
              <div class="skeleton-shimmer"></div>
            </div>
          }
        </div>
      }

      <!-- Error State -->
      @if (error && !isLoading) {
        <div class="gif-error">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32"
            viewBox="0 0 24 24" fill="none">
            <path
              d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-7v2h2v-2h-2zm0-8v6h2V7h-2z"
              fill="#e74c3c"
            />
          </svg>
          <p>{{ error }}</p>
        </div>
      }

      <!-- No Results State -->
      @if (!isLoading && gifResults.length === 0 && !error) {
        <div class="gif-no-results">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48"
            viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
              fill="#95a5a6"
            />
          </svg>
          <p>No GIFs found</p>
          <span>Try a different search term</span>
        </div>
      }

      <!-- GIF Grid -->
      @if (gifResults.length > 0 && !isLoading) {
        <div class="gif-results">
          <div class="gif-grid">
            @for (gif of gifResults; track gif) {
              <div class="gif-item" (click)="onSelectGif(gif)">
                <img [src]="gif" alt="GIF" loading="lazy" />
              </div>
            }
          </div>
        </div>
      }

      <!-- Load More -->
      @if (hasNext && !isLoading) {
        <div class="gif-load-more text-center mt-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            (click)="loadMore()"
          >
            Load more
          </button>
        </div>
      }

    </div>
  `
})
export class GifPanelComponent implements OnInit, OnChanges {
  @Input() visible = true;
  @Output() gifSelected = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();

  gifQuery = '';
  gifResults: string[] = [];
  currentPage = 1;
  hasNext = false;
  isLoading = false;
  error: string | null = null;
  readonly skeletonItems = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  constructor(private klipyService: KlipyService) { }

  ngOnInit(): void {
    this.loadTrending();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue === true) {
      if (this.gifQuery.trim()) {
        this.searchGifs();
      } else if (this.gifResults.length === 0) {
        this.loadTrending();
      }
    }
  }


  searchGifs(): void {
    const q = this.gifQuery.trim();
    if (!q) {
      this.loadTrending();
      return;
    }
    this.currentPage = 1;
    this.isLoading = true;
    this.error = null;
    this.klipyService.searchGifs(q, 1, 20).subscribe({
      next: (res) => {
        this.gifResults = res.gifs;
        this.hasNext = res.hasNext;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error searching Klipy GIFs', err);
        this.error = 'Unable to load GIFs. Please try again.';
        this.isLoading = false;
      },
    });
  }

  loadMore(): void {
    if (!this.gifQuery.trim() || !this.hasNext) return;
    this.currentPage++;
    this.isLoading = true;
    this.klipyService.searchGifs(this.gifQuery.trim(), this.currentPage, 20).subscribe({
      next: (res) => {
        this.gifResults = [...this.gifResults, ...res.gifs];
        this.hasNext = res.hasNext;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading more Klipy GIFs', err);
        this.error = 'Unable to load more GIFs.';
        this.isLoading = false;
      },
    });
  }

  onSelectGif(gifUrl: string): void {
    this.gifSelected.emit(gifUrl);
  }

  // ── private helpers ──────────────────────────────────────────────────────────

  private loadTrending(): void {
    this.isLoading = true;
    this.error = null;
    this.klipyService.getTrendingGifs(1, 20).subscribe({
      next: (res) => {
        this.gifResults = res.gifs;
        this.hasNext = res.hasNext;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading trending GIFs', err);
        this.error = 'Unable to load GIFs. Please try again.';
        this.isLoading = false;
      },
    });
  }
}
